import { useEffect, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import { useDispatch } from 'react-redux';
import styled from 'styled-components';
import { notify } from '../../../../../notify/notify-component';
import {
  IObj,
  PaymentOptions,
  businessAccountObj,
  paymentsGroupObj,
} from '../typeOptionsGroup/obj';
import { AppDispatch, useAppSelector } from '../../../../../../redux/store';
import {
  createPAymentTypePDVAsync,
  getPAymentTypePDVAsync,
  updatePAymentTypePDVAsync,
  getNextCodePDVAsync,
} from '../../../../../../redux/features/PaymentTypePDV/services';
import { notifyError } from '../../../../../notify/notify-component';
import { CheckboxHook } from '../../../../../reactHookForm/inputCheckbox';
import {
  IBrPaymentTypes,
  IBrPaymentTypesProviders,
  IPaymentMechanisms,
} from '../../../../../../redux/features/PaymentTypePDV/interface';
import Layout from '../../../../../data/Layout';
import { InputDefault } from '../../../../../data/inputDefault';
import ReactSelect from '../../../../../data/reactSelect';

// ─── Styled ──────────────────────────────────────────────────────────────────

const Wrapper = styled.div`
  margin: 1rem 1rem;

  @media only screen and (min-width: 1100px) {
    display: grid;
    grid-template-columns: 1fr 1fr;
    column-gap: 1rem;
    gap: 0.3rem;

    label > .label {
      text-align: right;
      margin-right: 0.5rem;
    }
  }

  .label {
    font-size: 11px;
    font-weight: 500;
    text-align: left;
  }

  label {
    display: grid;
    grid-template-columns: 1fr 3fr;
    text-align: left;

    @media only screen and (max-width: 600px) {
      grid-template-columns: 1fr;
      .label {
        display: none;
      }
    }
  }
`;

const Divider = styled.div`
  border-top: 1px solid #dee2e6;
  margin: 1.25rem 1rem 1rem;
`;

const SectionTitle = styled.p`
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: #6c757d;
  margin: 0 1rem 0.75rem;
`;

const WrapperCheckboxes = styled.div`
  margin: 0 1rem;
  display: flex;
  flex-direction: column;
  gap: 0.1rem;
`;

const ErrorMsg = styled.div`
  font-size: 10px;
  color: #dc2229;
  margin-top: 0.1rem;
  margin-left: 1px;
`;

// ─────────────────────────────────────────────────────────────────────────────

const pdvFormSchema = Yup.object().shape({
  description: Yup.string().required('Por favor, insira a descrição.'),

  payment_group: Yup.mixed().test(
    'payment_group',
    'Por favor, selecione o Grupo de Pagamento.',
    (value: any) => value !== null && value !== undefined,
  ),

  business_account_id: Yup.mixed().test(
    'business_account_id',
    'Por favor, selecione a Moeda.',
    (value: any) => value !== null && value !== undefined,
  ),

  payment_mechanism_id: Yup.mixed().test(
    'payment_mechanism_id',
    'Por favor, selecione o tipo de pagamento.',
    (value: any) => value !== null && value !== undefined,
  ),

  br_payment_type_id: Yup.mixed().test(
    'br_payment_type_id',
    'Por favor, selecione o Tipo de Pagamento (fiscal).',
    (value: any) => value !== null && value !== undefined,
  ),

  br_payment_type_provider_id: Yup.mixed().test(
    'br_payment_type_provider_id',
    'Por favor, selecione a Operadora.',
    (value: any) => value !== null && value !== undefined,
  ),
});

interface IProps {
  OnAddNewPDV: (e: boolean) => void;
  listEditeItem: PaymentOptions | undefined;
  setKeyActived: any;
}

interface IInput {
  code: any;
  id: number;
  sort_order: number;
  description: string;
  payment_group: any;
  br_payment_type_id: any;
  br_payment_type_description: string;
  payment_mechanism_id: any;
  br_payment_type_provider_id: any;
  business_account_id: any;
  inactive: boolean;
  visible_delivery: boolean;
  visible_xd_orders: boolean;
  requires_exchange: boolean;
  sent_to_checking_account: boolean;
}

export default function PdvForm({
  OnAddNewPDV,
  listEditeItem,
  setKeyActived,
}: IProps) {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  const dispatch = useDispatch<AppDispatch>();
  const { brPaymentTypes, brPaymentTypesProviders, paymentMechanisms } =
    useAppSelector(({ paymentTypePDVReducer }) => paymentTypePDVReducer.value);

  const [loading, setLoading] = useState<boolean>(false);

  const {
    register,
    handleSubmit,
    control,
    reset,
    watch,
    setValue,
    formState: { errors },
  } = useForm<any>({
    resolver: yupResolver(pdvFormSchema),
  });

  // ─── Opções dos selects ───────────────────────────────────────────────────

  const objBrPaymentTypes = brPaymentTypes.map((item: IBrPaymentTypes) => ({
    label: `${item.codigo} - ${item.descricao}`,
    value: item.id,
  }));

  const objPaymentMechanisms = paymentMechanisms.map((item: IPaymentMechanisms) => ({
    label: `${item.sigla} - ${item.descricao}`,
    value: item.id,
  }));

  const objBrPaymentTypesProviders = brPaymentTypesProviders.map(
    (item: IBrPaymentTypesProviders) => ({
      label: `${item.codigo} - ${item.descricao}`,
      value: item.id,
    }),
  );

  // ─── Handlers ────────────────────────────────────────────────────────────

  const handleCancel = () => OnAddNewPDV(false);

  const onSubmit: SubmitHandler<IInput> = async (data: IInput) => {
    setLoading(true);

    const newData = {
      ...data,
      store_id: storeMe?.storeActive?.id,
      br_payment_type_description:
        objBrPaymentTypes.find((o) => o.value === data.br_payment_type_id)?.label ?? '',
      br_payment_type_id: String(data.br_payment_type_id),
      br_payment_type_provider_id: String(data.br_payment_type_provider_id),
      business_account_id: data.business_account_id,
      payment_group: String(data.payment_group),
      payment_mechanism_id: String(data.payment_mechanism_id),
      requires_exchange: !!data.requires_exchange,
      sent_to_checking_account: !!data.sent_to_checking_account,
      visible_delivery: !!data.visible_delivery,
      visible_xd_orders: !!data.visible_xd_orders,
      visible_on_xd_orders: !!data.visible_xd_orders,
      inactive: false,
      currency_id: '1',
      cashier_account_id: '0',
      issue_direct_debit: '0',
      generate_mb_reference: '0',
    };

    if (listEditeItem) {
      const { id_pdv_payment, ...listEditeItemWithoutIdPdv } = listEditeItem as any;
      const newDataUpdate = {
        ...listEditeItemWithoutIdPdv,
        ...newData,
        id: listEditeItem?.id ?? Number(data.id),
        inactive: !!data.inactive,
      };
      dispatch(updatePAymentTypePDVAsync(newDataUpdate)).then((result: any) => {
        if (updatePAymentTypePDVAsync.fulfilled.match(result)) {
          notify('Forma de recebimento atualizada com sucesso!'); // 👈 adicionar
          dispatch(getPAymentTypePDVAsync(storeMe?.storeActive?.id));
          setKeyActived('pdv');
          OnAddNewPDV(false);
        } else {
          notifyError('Erro ao atualizar item do PDV.');
        }
        setLoading(false);
      });
      return;
    }

    dispatch(createPAymentTypePDVAsync(newData)).then((result: any) => {
      if (createPAymentTypePDVAsync.fulfilled.match(result)) {
        notify('Forma de recebimento criada com sucesso!'); // 👈 adicionar
        dispatch(getPAymentTypePDVAsync(storeMe?.storeActive?.id));
        setKeyActived('pdv');
        OnAddNewPDV(false);
      } else {
        notifyError('Erro ao adicionar item ao PDV.');
      }
      setLoading(false);
    });
  };

  // ─── Reset do form ────────────────────────────────────────────────────────

  useEffect(() => {
    if (listEditeItem) {
      reset({
        code: listEditeItem?.code,
        sort_order: listEditeItem?.sort_order,
        description: listEditeItem?.description,
        br_payment_type_description: listEditeItem?.br_payment_type_description,
        inactive: listEditeItem?.inactive,
        visible_delivery: listEditeItem?.visible_delivery,
        visible_xd_orders: (listEditeItem as any)?.visible_on_xd_orders,
        requires_exchange: listEditeItem?.requires_exchange,
        sent_to_checking_account: listEditeItem?.sent_to_checking_account,
        payment_group: paymentsGroupObj.find(
          (item: IObj) => item.value === String(listEditeItem?.payment_group),
        )?.value ?? null,
        payment_mechanism_id:
          objPaymentMechanisms.find(
            (item) => String(item.value) === String(listEditeItem?.payment_mechanism_id),
          )?.value ?? null,
        br_payment_type_id:
          objBrPaymentTypes.find(
            (item) => String(item.value) === String(listEditeItem?.br_payment_type_id),
          )?.value ?? null,
        br_payment_type_provider_id:
          objBrPaymentTypesProviders.find(
            (item) =>
              String(item.value) === String(listEditeItem?.br_payment_type_provider_id),
          )?.value ?? null,
        business_account_id:
          businessAccountObj.find(
            (item: IObj) => item.value === String(listEditeItem?.business_account_id),
          )?.value ?? null,
      });
    } else {
      dispatch(getNextCodePDVAsync(storeMe?.storeActive?.id)).then(
        (result: any) => {
          if (getNextCodePDVAsync.fulfilled.match(result)) {
            const next = result.payload.next_code;
            reset({
              code: next,
              sort_order: next,
              description: '',
              payment_group: paymentsGroupObj[0]?.value ?? null,
              br_payment_type_id: objBrPaymentTypes[0]?.value ?? null,
              br_payment_type_description: '',
              payment_mechanism_id: objPaymentMechanisms[0]?.value ?? null,
              br_payment_type_provider_id: objBrPaymentTypesProviders[0]?.value ?? null,
              business_account_id: businessAccountObj[0]?.value ?? null,
              inactive: false,
              visible_delivery: true,
              visible_xd_orders: false,
              requires_exchange: false,
              sent_to_checking_account: false,
            });
          }
        },
      );
    }
  }, [brPaymentTypes, paymentMechanisms, brPaymentTypesProviders, listEditeItem]);

  // ─── Render ───────────────────────────────────────────────────────────────

  return (
    <Layout
      title={listEditeItem ? 'Editar Forma de Recebimento' : 'Nova Forma de Recebimento'}
      isEditable={!!listEditeItem}
      onClickSave={handleSubmit(onSubmit)}
      onClickEdit={handleSubmit(onSubmit)}
      onClickCancel={handleCancel}
    >
      <Wrapper>
      <InputDefault
        label="Código"
        type="number"
        placeholder="Código"
        disabled={false}
        state={watch('code')}
        setState={(val: string) => setValue('code', val)}
      />

<div>
    <Controller
      name="payment_mechanism_id"
      control={control}
      render={({ field }) => (
        <ReactSelect
          label="Tipo de pagamento"
          placeholder="Tipo de pagamento"
          options={objPaymentMechanisms}
          state={field.value}
          setState={(val: any) => field.onChange(val)}
          isInvalid={!!errors.payment_mechanism_id}
        />
      )}
    />
    {errors.payment_mechanism_id && (
      <ErrorMsg>{errors.payment_mechanism_id.message as string}</ErrorMsg>
    )}

    
  </div>

  


  <div>
    <InputDefault
      label="Descrição"
      type="text"
      placeholder="Descrição"
      state={watch('description')}
      setState={(val: string) => setValue('description', val)}
      isInvalid={!!errors.description}
    />
    {errors.description && (
      <ErrorMsg>{errors.description.message as string}</ErrorMsg>
    )}
  </div>

  <div>
    <Controller
      name="business_account_id"
      control={control}
      render={({ field }) => (
        <ReactSelect
          label="Moeda"
          placeholder="Moeda"
          options={businessAccountObj.map((item: IObj) => ({
            label: item.label,
            value: item.value,
          }))}
          state={field.value}
          setState={(val: any) => field.onChange(val)}
          isInvalid={!!errors.business_account_id}
        />
      )}
    />
    {errors.business_account_id && (
      <ErrorMsg>{errors.business_account_id.message as string}</ErrorMsg>
    )}
  </div>

<Controller
      name="payment_group"
      control={control}
      render={({ field }) => (
        <ReactSelect
          label="Grupo"
          placeholder="Grupo de Pagamento"
          options={paymentsGroupObj.map((item: IObj) => ({
            label: item.label,
            value: item.value,
          }))}
          state={field.value}
          setState={(val: any) => field.onChange(val)}
          isInvalid={!!errors.payment_group}
        />
      )}
    />
    {errors.payment_group && (
      <ErrorMsg>{errors.payment_group.message as string}</ErrorMsg>
    )}


  
  <div>

        <InputDefault
  label="Ordem"
  type="number"
  placeholder="Ordem"
  state={watch('sort_order')}
  setState={(val: string) => setValue('sort_order', val)}
/>


    
  </div>

  <div>
    
    <Controller
      name="br_payment_type_id"
      control={control}
      render={({ field }) => (
        <ReactSelect
          label="Tipo de pagamento (fiscal)"
          placeholder="Tipo de pagamento (fiscal)"
          options={objBrPaymentTypes}
          state={field.value}
          setState={(val: any) => field.onChange(val)}
          isInvalid={!!errors.br_payment_type_id}
        />
      )}
    />
    {errors.br_payment_type_id && (
      <ErrorMsg>{errors.br_payment_type_id.message as string}</ErrorMsg>
    )}



    
    <Controller
      name="br_payment_type_provider_id"
      control={control}
      render={({ field }) => (
        <ReactSelect
          label="Operadora de CC/CD (fiscal)"
          placeholder="Operadora de CC/CD (fiscal)"
          options={objBrPaymentTypesProviders}
          state={field.value}
          setState={(val: any) => field.onChange(val)}
          isInvalid={!!errors.br_payment_type_provider_id}
        />
      )}
    />
    {errors.br_payment_type_provider_id && (
      <ErrorMsg>{errors.br_payment_type_provider_id.message as string}</ErrorMsg>
    )}
  </div>

  

</Wrapper>

      <Divider />
      <SectionTitle>Opções de visibilidade</SectionTitle>

      <WrapperCheckboxes>
        <CheckboxHook
          register={register}
          errors={[errors]}
          label="Visível no Delivery"
          name="visible_delivery"
        />
        <CheckboxHook
          register={register}
          errors={[errors]}
          label="Visível no XD Orders"
          name="visible_xd_orders"
        />
        <CheckboxHook
          register={register}
          errors={[errors]}
          label="Requer Troco"
          name="requires_exchange"
        />
        <CheckboxHook
          register={register}
          errors={[errors]}
          label="Envia para Conta Corrente"
          name="sent_to_checking_account"
        />
        {listEditeItem && (
          <CheckboxHook
            register={register}
            errors={[errors]}
            label="Inativo"
            name="inactive"
          />
        )}
      </WrapperCheckboxes>
    </Layout>
  );
}